import { useState, useMemo } from 'react';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  FileText,
  Minus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { ScoreBadge } from '@/features/evaluation/components/ScoreBar';
import type {
  EvaluationWithDetails,
  CitedPassage,
} from '@/features/evaluation/types';

// =========================================
// ComparisonView — side-by-side source table
// =========================================

interface ComparisonViewProps {
  evaluations: EvaluationWithDetails[];
  className?: string;
}

interface CriteriaRow {
  criteria_id: string;
  criteria_name: string;
  criteria_type: string;
  scores: Map<string, CellData>;
}

interface CellData {
  raw_score: number;
  weighted_score: number;
  weight: number;
  max_weighted: number;
  explanation: string | null;
  passages: CitedPassage[];
}

type SortField = 'name' | 'score';
type SortOrder = 'asc' | 'desc';

export function ComparisonView({ evaluations, className }: ComparisonViewProps) {
  const [expandedCriteria, setExpandedCriteria] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Build the comparison matrix
  const { sources, criteriaRows } = useMemo(() => {
    const sourceList = evaluations.map((ev) => ({
      id: ev.source_id,
      evaluation_id: ev.id,
      name: ev.source?.title || ev.source?.file_name || 'Untitled',
      authors: ev.source?.authors ?? [],
      score: ev.breakdown?.percentage ?? 0,
    }));

    // Sort sources
    const sorted = [...sourceList].sort((a, b) => {
      const mul = sortOrder === 'asc' ? 1 : -1;
      if (sortField === 'name') return mul * a.name.localeCompare(b.name);
      return mul * (a.score - b.score);
    });

    // Collect all criteria across evaluations
    const criteriaMap = new Map<string, CriteriaRow>();

    for (const ev of evaluations) {
      if (!ev.breakdown?.weighted_scores) continue;

      for (const ws of ev.breakdown.weighted_scores) {
        if (!criteriaMap.has(ws.criteria_id)) {
          criteriaMap.set(ws.criteria_id, {
            criteria_id: ws.criteria_id,
            criteria_name: ws.criteria_name,
            criteria_type: ws.criteria_type,
            scores: new Map(),
          });
        }

        const criterionResult = ev.criteria_results?.find(
          (cr) => cr.criteria_id === ws.criteria_id
        );

        criteriaMap.get(ws.criteria_id)!.scores.set(ev.id, {
          raw_score: ws.raw_score,
          weighted_score: ws.weighted_score,
          weight: ws.weight,
          max_weighted: ws.max_weighted,
          explanation: criterionResult?.explanation ?? null,
          passages: criterionResult?.passages ?? [],
        });
      }
    }

    return {
      sources: sorted,
      criteriaRows: Array.from(criteriaMap.values()),
    };
  }, [evaluations, sortField, sortOrder]);

  const toggleCriteria = (id: string) => {
    setExpandedCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'score' ? 'desc' : 'asc');
    }
  };

  if (evaluations.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-12 text-center">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Select sources to compare their evaluations.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Comparison Table
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {sources.length} sources &middot; {criteriaRows.length} criteria
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            {/* Header: source columns */}
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left min-w-[200px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 font-semibold text-slate-700 hover:text-slate-900"
                    onClick={() => toggleSort('name')}
                  >
                    Criteria
                  </Button>
                </th>
                {sources.map((source) => (
                  <th
                    key={source.evaluation_id}
                    className="px-4 py-3 text-center min-w-[160px] max-w-[200px]"
                  >
                    <div className="space-y-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="font-medium text-slate-700 truncate text-xs">
                            {source.name}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="text-xs">{source.name}</p>
                          {source.authors.length > 0 && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              {source.authors.join(', ')}
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                      <ScoreBadge score={source.score} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* Overall score row */}
              <tr className="border-b bg-slate-50/50 font-medium">
                <td className="sticky left-0 z-10 bg-slate-50/50 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 font-semibold text-slate-700 hover:text-slate-900"
                      onClick={() => toggleSort('score')}
                    >
                      Overall Score
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                </td>
                {sources.map((source) => (
                  <td key={source.evaluation_id} className="px-4 py-2.5 text-center">
                    <ScoreBadge score={source.score} />
                  </td>
                ))}
              </tr>

              {/* Criteria rows */}
              {criteriaRows.map((row) => {
                const isExpanded = expandedCriteria.has(row.criteria_id);

                return (
                  <CriteriaComparisonRow
                    key={row.criteria_id}
                    row={row}
                    sources={sources}
                    isExpanded={isExpanded}
                    onToggle={() => toggleCriteria(row.criteria_id)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// =========================================
// CriteriaComparisonRow — expandable row
// =========================================

interface CriteriaComparisonRowProps {
  row: CriteriaRow;
  sources: Array<{ id: string; evaluation_id: string; name: string }>;
  isExpanded: boolean;
  onToggle: () => void;
}

function CriteriaComparisonRow({
  row,
  sources,
  isExpanded,
  onToggle,
}: CriteriaComparisonRowProps) {
  return (
    <>
      <tr className="border-b hover:bg-slate-50/50 transition-colors">
        <td className="sticky left-0 z-10 bg-white hover:bg-slate-50/50 px-4 py-2.5">
          <button
            type="button"
            className="flex items-center gap-2 text-left w-full"
            onClick={onToggle}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            )}
            <span className="text-slate-700 font-medium truncate">
              {row.criteria_name}
            </span>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
              {row.criteria_type.replace('_', ' ')}
            </Badge>
          </button>
        </td>
        {sources.map((source) => {
          const cell = row.scores.get(source.evaluation_id);
          return (
            <td key={source.evaluation_id} className="px-4 py-2.5 text-center">
              {cell ? (
                <ScoreCell
                  rawScore={cell.raw_score}
                  maxWeighted={cell.max_weighted}
                  weight={cell.weight}
                />
              ) : (
                <Minus className="h-4 w-4 text-slate-300 mx-auto" />
              )}
            </td>
          );
        })}
      </tr>

      {/* Expanded detail row */}
      {isExpanded && (
        <tr className="border-b bg-slate-50/30">
          <td className="sticky left-0 z-10 bg-slate-50/30 px-4 py-2 text-xs text-slate-500 align-top">
            Explanations
          </td>
          {sources.map((source) => {
            const cell = row.scores.get(source.evaluation_id);
            return (
              <td key={source.evaluation_id} className="px-4 py-2 align-top">
                {cell ? (
                  <div className="space-y-1.5 text-xs">
                    {cell.explanation && (
                      <p className="text-slate-600 italic leading-relaxed">
                        {cell.explanation}
                      </p>
                    )}
                    {cell.passages.length > 0 && (
                      <div className="space-y-1">
                        {cell.passages.slice(0, 2).map((p, i) => (
                          <blockquote
                            key={i}
                            className="border-l-2 border-indigo-300 pl-2 text-[11px] text-slate-500 line-clamp-3"
                          >
                            &ldquo;{p.text}&rdquo;
                            {p.page !== null && (
                              <span className="text-slate-400 ml-0.5">
                                (p.{p.page})
                              </span>
                            )}
                          </blockquote>
                        ))}
                        {cell.passages.length > 2 && (
                          <p className="text-[10px] text-slate-400">
                            +{cell.passages.length - 2} more
                          </p>
                        )}
                      </div>
                    )}
                    {!cell.explanation && cell.passages.length === 0 && (
                      <p className="text-slate-400">No details</p>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-slate-400">N/A</span>
                )}
              </td>
            );
          })}
        </tr>
      )}
    </>
  );
}

// =========================================
// ScoreCell — color-coded cell value
// =========================================

interface ScoreCellProps {
  rawScore: number;
  weight: number;
  maxWeighted: number;
}

function ScoreCell({ rawScore, weight, maxWeighted }: ScoreCellProps) {
  const weighted = rawScore * weight;
  const pct = maxWeighted > 0 ? Math.round((weighted / maxWeighted) * 100) : 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="inline-flex flex-col items-center gap-0.5">
          <span
            className={cn(
              'text-sm font-semibold tabular-nums',
              pct >= 70 && 'text-emerald-600',
              pct >= 40 && pct < 70 && 'text-amber-600',
              pct < 40 && 'text-rose-600'
            )}
          >
            {rawScore.toFixed(1)}
          </span>
          <div className="h-1 w-10 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                pct >= 70 && 'bg-emerald-500',
                pct >= 40 && pct < 70 && 'bg-amber-500',
                pct < 40 && 'bg-rose-500'
              )}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">
          {rawScore.toFixed(1)} x{weight} = {weighted.toFixed(1)}/{maxWeighted}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
